import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import { type StudioSegment } from "@fitness-app/data-models/entities/StudioClip";
import { getErrorMessage } from "@fitness-app/utils";

import { backgroundTasksApi, supabase } from "~/store/initializeStore";
import { STUDIO_CLIPS_QUERY_KEY } from "./useStudioData";

/**
 * Ads from segments (TRE-1975, etap G): the agent mines hook / value / cta blocks of a
 * recording (one production request in mode "segments"), the panel composes variants, and
 * renders only the ones somebody wants to see finished.
 */

export const STUDIO_SEGMENTS_QUERY_KEY = ["studio", "segments"];

export const useStudioSegments = (requestId: string | null) =>
  useQuery([...STUDIO_SEGMENTS_QUERY_KEY, requestId ?? ""], {
    enabled: Boolean(requestId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("studio_segment")
        .select("*")
        .eq("requestId", requestId!)
        .order("role")
        .order("score", { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as StudioSegment[];
    },
  });

export const useComposeVariants = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      requestId: string;
      hookIds: string[];
      valueIds: string[];
      ctaIds: string[];
      limit: number;
    }) => {
      const response = await backgroundTasksApi.post<{ variantIds: string[] }>(
        "/api/trainers/ai/compose-studio-variants",
        input,
      );
      return response.data;
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries(STUDIO_CLIPS_QUERY_KEY);
      void message.success(`Złożone warianty: ${result.variantIds.length}. Podejrzyj je i wyrenderuj wybrane.`);
    },
    onError: (error) => {
      void message.error(`Nie udało się złożyć wariantów: ${getErrorMessage(error)}`);
    },
  });
};

export const useRenderClips = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clipIds: string[]) => {
      const response = await backgroundTasksApi.post<{ dispatched: string[] }>("/api/trainers/ai/render-studio-clips", {
        clipIds,
      });
      return response.data;
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries(STUDIO_CLIPS_QUERY_KEY);
      void message.success(`Render uruchomiony dla ${result.dispatched.length} wariantów.`);
    },
    onError: (error) => {
      void message.error(`Nie udało się uruchomić renderu: ${getErrorMessage(error)}`);
    },
  });
};
